// LeanQuest.jsx
// Solo, responsive, front-end 'Lean Quest' adventure game for farmers.
// Place in src/components/ and import with: `import LeanQuest from './components/LeanQuest'` then use <LeanQuest />.

import React, { useEffect, useState } from 'react';

// -------- CONFIG --------
const INITIAL_ENERGY = 5; // actions per day
const INITIAL_MONEY = 200;
const MAX_INVENTORY = 12;
const QUESTS = [
  {
    id: 'pest-1',
    title: 'Aphid Attack on Mustard',
    desc: 'Small green insects found on mustard leaves. Yields dropping in parts of the field.',
    difficulty: 2,
    rewards: { money: 80, xp: 12 },
    options: [
      { id: 'spray-pesticide', title: 'Spray chemical pesticide', cost: 40, energy:1, success: 0.9, consequence: 'Fast but costly.' },
      { id: 'introduce-predator', title: 'Release ladybugs (biocontrol)', cost: 20, energy:1, success: 0.75, consequence: 'Eco-friendly, slightly slower.' },
      { id: 'do-nothing', title: 'Wait and monitor', cost: 0, energy:0, success: 0.35, consequence: 'Risky — might lose yield.' }
    ]
  },
  {
    id: 'water-1',
    title: 'Irrigation Scheduling Problem',
    desc: 'Monsoon is patchy this season; soil moisture varies. Need to decide irrigation schedule for wheat.',
    difficulty: 1,
    rewards: { money: 40, xp: 6 },
    options: [
      { id: 'irrigate-everyday', title: 'Irrigate every day', cost: 15, energy:1, success: 0.85, consequence: 'Safe but uses water & cost.' },
      { id: 'smart-irrigation', title: 'Use drip tickets (precision)', cost: 25, energy:1, success: 0.9, consequence: 'Efficient, upfront cost.' },
      { id: 'skip', title: 'Skip irrigation', cost: 0, energy:0, success: 0.4, consequence: 'Risk of crop stress.' }
    ]
  },
  {
    id: 'market-1',
    title: 'Sudden Price Drop for Tomatoes',
    desc: 'Nearby market flooded with tomatoes. Decide whether to store, sell at low price, or process into paste.',
    difficulty: 3,
    rewards: { money: 120, xp: 18 },
    options: [
      { id: 'sell-low', title: 'Sell immediately at low price', cost: 0, energy:0, success: 0.9, consequence: 'Quick cash but low profit.' },
      { id: 'store-inventory', title: 'Store in cold room for 3 days', cost: 30, energy:0, success: 0.65, consequence: 'Chance prices recover.' },
      { id: 'process-paste', title: 'Process into tomato paste', cost: 40, energy:1, success: 0.8, consequence: 'Value-add but needs machinery.' }
    ]
  },
  {
    id: 'soil-1',
    title: 'Soil Nutrient Deficiency',
    desc: 'Leaves are pale; test shows low nitrogen. Choose fertilizer strategy.',
    difficulty: 2,
    rewards: { money: 70, xp: 10 },
    options: [
      { id: 'urea', title: 'Apply urea (quick N)', cost: 25, energy:1, success: 0.9, consequence: 'Fast effect, risk of burns.' },
      { id: 'compost', title: 'Apply compost', cost: 10, energy:1, success: 0.7, consequence: 'Slow but soil-friendly.' },
      { id: 'foliar', title: 'Foliar spray', cost: 15, energy:1, success: 0.75, consequence: 'Good for quick correction.' }
    ]
  }
];

// helper
function rand() { return Math.random(); }

export default function LeanQuest(){
  const [day, setDay] = useState(1);
  const [energy, setEnergy] = useState(INITIAL_ENERGY);
  const [money, setMoney] = useState(INITIAL_MONEY);
  const [xp, setXp] = useState(0);
  const [level, setLevel] = useState(1);
  const [inventory, setInventory] = useState([]); // simple list of items
  const [availableQuests, setAvailableQuests] = useState(() => shuffleArray([...QUESTS]));
  const [log, setLog] = useState([]);
  const [message, setMessage] = useState('Welcome to Lean Quest — solve farming challenges and level up!');
  const [selectedQuest, setSelectedQuest] = useState(null);
  const [tutorialShown, setTutorialShown] = useState(() => { return localStorage.getItem('krishi_quest_seen')==='1'; });
  const [showHelp, setShowHelp] = useState(false);

  // load save
  useEffect(()=>{
    try{
      const saved = JSON.parse(localStorage.getItem('krishi_quest_v1')||'null');
      if(saved){
        setDay(saved.day); setEnergy(saved.energy); setMoney(saved.money); setXp(saved.xp); setLevel(saved.level);
        setInventory(saved.inventory||[]); setAvailableQuests(saved.availableQuests||shuffleArray([...QUESTS])); setLog(saved.log||[]);
      }
    }catch(e){}
  },[]);

  // save state
  useEffect(()=>{
    localStorage.setItem('krishi_quest_v1', JSON.stringify({day,energy,money,xp,level,inventory,availableQuests,log}));
  },[day,energy,money,xp,level,inventory,availableQuests,log]);

  useEffect(()=>{
    // level by xp
    const next = 10 + (level-1)*15;
    if(xp >= next){ setLevel(l=>l+1); setXp(x=>x-next); setMessage('Level up! New farming skills unlocked.'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[xp]);

  function shuffleArray(a){
    for(let i=a.length-1;i>0;i--){ const j=Math.floor(Math.random()*(i+1)); [a[i],a[j]]=[a[j],a[i]]; } return a;
  }

  function startQuest(q){
    if(energy <= 0){ setMessage('No energy — rest or advance day to regain energy.'); return; }
    setSelectedQuest(q);
    setMessage(`Selected quest: ${q.title}`);
  }

  function performOption(opt){
    if(!selectedQuest) return;
    if(energy < (opt.energy||0)){ setMessage('Not enough energy for this action.'); return; }
    // apply cost
    setMoney(m => m - (opt.cost||0));
    setEnergy(e => Math.max(0, e - (opt.energy||0)));

    // success check
    const base = opt.success || 0.5;
    const difficultyFactor = Math.max(0.2, 1 - (selectedQuest.difficulty*0.12));
    const levelBonus = 0.02 * Math.max(0, level-1);
    const finalProb = Math.min(0.98, base * difficultyFactor + levelBonus);

    const roll = rand();
    let success = roll < finalProb;

    // special inventory influence
    if(!success && inventory.includes('bio-predator') && opt.id==='introduce-predator'){
      // small boost
      success = rand() < (finalProb + 0.12);
    }

    if(success){
      // reward
      setMoney(m=>m + selectedQuest.rewards.money);
      setXp(x=>x + selectedQuest.rewards.xp);
      setLog(l => [{type:'success', text:`${selectedQuest.title} — Success with '${opt.title}'. Earned ₹${selectedQuest.rewards.money}.`}, ...l].slice(0,50));
      setMessage(`Success! ${selectedQuest.rewards.money} added.`);
      // sometimes give item
      if(Math.random() < 0.18){ const itm = Math.random()<0.5? 'bio-predator':'fertilizer-pouch'; addItem(itm); setMessage(m=>m + ` Found item: ${itm}`); }
    } else {
      setLog(l => [{type:'fail', text:`${selectedQuest.title} — Failed '${opt.title}'. Lost ₹${opt.cost||0}.`}, ...l].slice(0,50));
      setMessage('Action did not resolve the issue — try another approach or advance the day.');
    }

    // mark quest completed/rotated out
    setAvailableQuests(qs => qs.filter(q=>q.id !== selectedQuest.id));
    setSelectedQuest(null);
  }

  function addItem(item){ setInventory(inv => { if(inv.length >= MAX_INVENTORY) { setMessage('Inventory full — cannot add item.'); return inv; } return [item, ...inv]; }); }
  function handleUseItem(idx){ const item = inventory[idx]; if(!item) return; if(item==='fertilizer-pouch'){ setXp(x=>x+5); setMessage('Used fertilizer pouch — +5 XP'); } if(item==='bio-predator'){ setMessage('Used bio-predator in next relevant quest automatically.'); }
    setInventory(inv => inv.filter((_,i)=> i!==idx)); }

  function rest(){ setDay(d=>d+1); setEnergy(INITIAL_ENERGY + Math.floor(level/2)); setMessage('New day: energy restored.');
    // small market/event simulation: maybe add small money from micro-contracts
    if(Math.random() < 0.15){ const bonus = Math.floor(Math.random()*50)+10; setMoney(m=>m+bonus); setLog(l=>[{type:'info', text:`Micro-contract: earned ₹${bonus} from a local cooperative.`}, ...l]); }
    // refill quests if low
    setAvailableQuests(qs => { if(qs.length < 2) return [...qs, ...shuffleArray([...QUESTS]).slice(0,2)]; return qs; }); }

  function abandonQuest(qid){ setAvailableQuests(qs=>qs.filter(q=>q.id!==qid)); setMessage('Quest abandoned.'); }

  function clearLog(){ setLog([]); }
  function resetGame(){ if(!window.confirm('Reset Lean Quest?')) return; localStorage.removeItem('krishi_quest_v1'); window.location.reload(); }

  // UI small helpers
  function formatMoney(n){ return `₹${n}`; }

  return (
    <div style={{padding:16, maxWidth:1100, margin:'0 auto', fontFamily:'system-ui,Segoe UI,Roboto,Helvetica,Arial'}}>
      <style>{`
        .top{display:flex; gap:12px; align-items:center; justify-content:space-between; flex-wrap:wrap}
        .panel{background:#fff; padding:10px; border-radius:10px; box-shadow:0 6px 18px rgba(0,0,0,0.06)}
        .quests{display:grid; gap:10px; grid-template-columns:repeat(auto-fit, minmax(240px,1fr))}
        .quest-card{padding:12px; border-radius:8px; cursor:pointer}
        .controls{display:grid; grid-template-columns:repeat(2,1fr); gap:8px}
        .inventory{display:flex; gap:6px; flex-wrap:wrap}
        .log{max-height:160px; overflow:auto}
        @media(max-width:760px){ .controls{grid-template-columns:1fr} }
      `}</style>

      <div className="top">
        <div style={{display:'flex', gap:10, alignItems:'center'}}>
          <h2 style={{margin:0}}>🧭 Lean Quest</h2>
          <div className="panel">Day: <strong>{day}</strong></div>
          <div className="panel">Energy: <strong>{energy}</strong></div>
          <div className="panel">Money: <strong>{formatMoney(money)}</strong></div>
          <div className="panel">Level: <strong>{level}</strong></div>
        </div>

        <div style={{display:'flex', gap:8, alignItems:'center'}}>
          <button onClick={rest}>Advance Day</button>
          <button onClick={clearLog}>Clear Log</button>
          <button onClick={resetGame}>Reset</button>
        </div>
      </div>

      <div className="panel mt-2 mb-2 flex flex-col items-center justify-center" style={{marginTop:12}}>
        <button onClick={() => setShowHelp(h => !h)} className="bg-cyan-600 text-white px-3 py-1 rounded">
            {showHelp ? 'Hide Help' : 'Help'}
          </button>
        {/* <h3 className="text-green-700 mb-2" style={{marginTop:0}}>Lean Quest — Tutorial</h3> */}
        {showHelp && (
          <div className='mt-2'>
            <p className='text-black'>Welcome to <strong>Lean Quest</strong>! This is a farming-adventure simulation where you solve real-world farm challenges.</p>
            <ul style={{paddingLeft:20}}>
              <li><strong>Day & Energy:</strong> Each day you have limited energy to perform actions.</li>
              <li><strong>Money & Inventory:</strong> Manage your funds and useful items (bio-predator, fertilizer, etc.) to improve success chances.</li>
              <li><strong>Quests:</strong> Select available quests, choose an action, and see results. Success gives money and XP.</li>
              <li><strong>Leveling:</strong> Earn XP to level up, which improves your chance of success.</li>
              <li><strong>Actions:</strong> Buy items, rest to restore energy, and plan eco-friendly approaches for bonus XP.</li>
              <li><strong>Tips:</strong> Read quest consequences carefully. Some options are fast but costly, others eco-friendly but slower.</li>
              <li><strong>Log:</strong> Track all your actions, successes, and failures in the activity log.</li>
            </ul>
          </div>
        )}
      </div>

      <div style={{display:'grid', gridTemplateColumns:'2fr 1fr', gap:12, marginTop:12}}>
        <div>
          <div className="panel">
            <h3 className="text-blue-700 mb-2" style={{marginTop:0}}>Available Quests</h3>
            <div className="quests">
              {availableQuests.map(q=> (
                <div key={q.id} className="quest-card panel" style={{border: '1px solid #eee'}}>
                  <h4 style={{margin:'0 0 6px 0'}}>{q.title}</h4>
                  <div style={{fontSize:13, color:'#444'}}>{q.desc}</div>
                  <div style={{marginTop:8, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <div style={{fontSize:13}}>Rewards: ₹{q.rewards.money} • XP {q.rewards.xp}</div>
                    <div style={{display:'flex', gap:6}}>
                      <button onClick={() => startQuest(q)}>Start</button>
                      <button onClick={() => abandonQuest(q.id)}>Skip</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{marginTop:12}} className="panel">
            <h3 className="text-blue-700 mb-2" style={{marginTop:0}}>Quest Detail</h3>
            {selectedQuest ? (
              <div>
                <h4 style={{margin:'0 0 6px 0'}}>{selectedQuest.title}</h4>
                <div style={{color:'#444'}}>{selectedQuest.desc}</div>
                <div style={{marginTop:8}}>
                  <strong>Options</strong>
                  <div style={{display:'grid', gap:8, marginTop:8}}>
                    {selectedQuest.options.map(opt=> (
                      <button key={opt.id} className="panel" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}} onClick={() => performOption(opt)}>
                        <div>
                          <div style={{fontWeight:600}}>{opt.title}</div>
                          <div style={{fontSize:12, color:'#666'}}>{opt.consequence}</div>
                        </div>
                        <div style={{textAlign:'right'}}>
                          <div style={{fontSize:13}}>Cost: ₹{opt.cost}</div>
                          <div style={{fontSize:12}}>Energy: {opt.energy}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div>Select a quest to see options and act.</div>
            )}
          </div>
        </div>

        <div>
          <div className="panel">
            <h3 className="text-blue-700 mb-2" style={{marginTop:0}}>Inventory</h3>
            <div className="inventory">
              {inventory.length === 0 ? <div style={{color:'#666'}}>No items</div> : inventory.map((it,idx)=> (
                <div key={idx} className="panel" style={{padding:'6px 8px'}}>
                  <div style={{fontSize:13}}>{it}</div>
                  <div style={{marginTop:6}}><button className="panel" onClick={() => handleUseItem(idx)}>Use</button></div>
                </div>
              ))}
            </div>
          </div>

          <div className="panel" style={{marginTop:12}}>
            <h3 className="text-blue-700 mb-2" style={{marginTop:0}}>Quick Actions</h3>
            <div className="controls">
              <button onClick={() => { if(money>=50){ setMoney(m=>m-50); addItem('bio-predator'); setMessage('Bought bio-predator.'); } else setMessage('Not enough money.'); }}>Buy Bio-predator (₹50)</button>
              <button onClick={() => { if(money>=30){ setMoney(m=>m-30); addItem('fertilizer-pouch'); setMessage('Bought fertilizer pouch.'); } else setMessage('Not enough money.'); }}>Buy Fertilizer (₹30)</button>
              <button onClick={() => { setEnergy(e=>Math.min(10, e+2)); setMessage('Rested: +2 energy'); }}>Quick Rest (+2 Energy)</button>
              <button onClick={() => { setMessage('Tip: Try eco-friendly options — they build XP.'); }}>Tip</button>
            </div>
          </div>

          <div className="panel" style={{marginTop:12}}>
            <h3 className="text-blue-700 mb-2" style={{marginTop:0}}>Activity Log</h3>
            <div className="log">
              {log.length===0 ? <div style={{color:'#666'}}>No activity yet</div> : (
                <ul style={{paddingLeft:16, margin:0}}>
                  {log.map((l,idx)=> <li key={idx} style={{marginBottom:6}}>{l.text}</li>)}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-gray-100" style={{marginTop:12, color:'#555'}}>Lean Quest — solo farming-adventure. Save loads automatically in localStorage.</footer>
    </div>
  );
}
